import React, {Fragment, useCallback} from 'react';

import {
	Alert,
	AlertTitle,
	Box,
	Button,
	CircularProgress,
	Snackbar,
	SnackbarOrigin,
	Stack,
	useTheme,
} from '@mui/material';
import {makeGetAccessUserManualLink} from 'main/factories/usecases/private/external-links/remote-user-manual/get-access-user-manual-factory';
import {makeGetFeaturesUserManualLink} from 'main/factories/usecases/private/external-links/remote-user-manual/get-features-user-manual-factory';
import {makeGetRefundUserManualLink} from 'main/factories/usecases/private/external-links/remote-user-manual/get-refund-user-manual-factory';
import hapvidaIconRefund from 'presentation/assets/images/hapvida-icon-refund.svg';
import hapvidaIcon from 'presentation/assets/images/hapvida-icon.svg';
import {useGetEndpointUrl, useGetManualUrl, useHostName} from 'presentation/hooks';
import Icon from 'presentation/icons';
import {useMediaQueries} from 'presentation/pages';
import {useExternalServiceStore} from 'presentation/pages/private/store/externalSerivceUrlStore';
import {translations} from 'presentation/translations-new';
import {AlertModal} from '../AlertModal';
import {StyledDescriptionTypography, StyledStack, StyledTypography} from './styles';
import {UserManualAlertProps} from './types';

const {
	accessDescription,
	featuresDescription,
	refundDescription,
	refundErrorTitle,
	refundErrorDescription,
	action,
} = translations['pt-br'].others.userManualAlert;
const {serverError, notice, serverErrorTitle, tryAgain} = translations['pt-br'].commons;

export function UserManualAlert({type, dataTagId}: UserManualAlertProps) {
	const theme = useTheme();
	const {isMobile} = useMediaQueries();
	const {dataHostNameState, isNotredameSaude} = useHostName();

	const isAccessUserManual = type === 'access';
	const isRefundManual = type === 'refund';
	const isRefundDesktopIcon = isRefundManual && !isMobile;

	let description = featuresDescription;
	if (isAccessUserManual) {
		description = accessDescription;
	}
	if (isRefundManual) {
		description = refundDescription;
	}
	const snackPosition: SnackbarOrigin = isMobile
		? {vertical: 'top', horizontal: 'center'}
		: {vertical: 'top', horizontal: 'right'};

	const {isError, setIsError, getLink, isLoading} = useGetEndpointUrl(description);
	const {
		isError: isManualError,
		getManualLink,
		setIsError: setManualIsError,
		isLoading: isManualLoading,
	} = useGetManualUrl(description);
	const cachedURL = useExternalServiceStore(state => state.urls[description]);
	const isRefundManualError = isRefundManual && isError;

	const handleOpenUserManual = useCallback(() => {
		setIsError(false);
		setManualIsError(false);
		if (cachedURL) {
			window.open(cachedURL, '_blank', 'noopener,noreferrer');
			return;
		}

		if (isRefundManual) {
			getLink(makeGetRefundUserManualLink);
			return;
		}

		let factory = makeGetFeaturesUserManualLink;

		if (isAccessUserManual) {
			factory = makeGetAccessUserManualLink;
		}

		const params = isAccessUserManual
			? {
					cdOperadora: dataHostNameState.productCode.join(','),
					...(isNotredameSaude && {isPpo: true}),
				}
			: undefined;

		getManualLink(factory, params);
	}, [cachedURL, isAccessUserManual, isRefundManual, dataHostNameState]);

	const handleCloseModal = () => {
		setIsError(false);
		setManualIsError(false);
	};

	const getBackgroundImage = () => {
		if (isNotredameSaude) {
			return 'none';
		}

		if (isRefundDesktopIcon) {
			return `url("${hapvidaIconRefund}")`;
		}
		return `url("${hapvidaIcon}")`;
	};

	return (
		<Fragment>
			<StyledStack
				direction="row"
				isRefundDesktopIcon={isRefundDesktopIcon}
				style={{
					backgroundImage: getBackgroundImage(),
				}}>
				<Box>
					<Icon name="help-outline-filled" size="22" color={theme.brand.primary.blue} />
				</Box>
				<Stack>
					<StyledDescriptionTypography variant="body2">
						{description}
					</StyledDescriptionTypography>
					<Stack gap={1} direction="row" alignItems="center">
						<StyledTypography
							data-tag-id={dataTagId}
							variant="body2"
							isLoading={isLoading || isManualLoading}
							onClick={handleOpenUserManual}>
							{action}
						</StyledTypography>
						{isLoading || isManualLoading ? (
							<CircularProgress
								sx={{color: theme.colors.light.text.disabled}}
								size={16}
							/>
						) : null}
					</Stack>
				</Stack>
			</StyledStack>
			{isRefundManualError ? (
				<Snackbar anchorOrigin={snackPosition} open={isRefundManualError}>
					<Alert sx={{maxWidth: 320, textAlign: 'left'}} severity="error">
						<AlertTitle>{refundErrorTitle}</AlertTitle>
						{refundErrorDescription}
					</Alert>
				</Snackbar>
			) : (
				<AlertModal
					open={isError || isManualError}
					onClose={handleCloseModal}
					description={serverError}
					title={serverErrorTitle}
					headerTitle={notice}
					variant="info"
					footerContent={
						<Button
							variant="contained"
							size="large"
							fullWidth
							onClick={handleOpenUserManual}>
							{tryAgain}
						</Button>
					}
				/>
			)}
		</Fragment>
	);
}
